import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Play, Power } from "lucide-react";
import {
  ApiError,
  bootSimulator,
  listSimulators,
  shutdownSimulator,
  type SimulatorInfo,
} from "../lib/api";

// Sort: Booted first (so the actionable "Shutdown" buttons are
// clustered), then by iOS version descending (newer first feels more
// useful than alphabetical), then by name.
function sortSims(list: SimulatorInfo[]): SimulatorInfo[] {
  return [...list].sort((a, b) => {
    const aBooted = a.state === "Booted";
    const bBooted = b.state === "Booted";
    if (aBooted !== bBooted) return aBooted ? -1 : 1;
    const av = a.osVersion || "";
    const bv = b.osVersion || "";
    if (av !== bv) return bv.localeCompare(av, undefined, { numeric: true });
    return a.name.localeCompare(b.name);
  });
}

export function SimulatorManager() {
  const [open, setOpen] = useState(false);
  const [sims, setSims] = useState<SimulatorInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Platform support probe. `null` = not yet checked, `true` = macOS
  // server, `false` = Linux/Windows server (we hide entirely).
  const [supported, setSupported] = useState<boolean | null>(null);
  // Per-UDID pending action so each row can show its own spinner.
  const [pending, setPending] = useState<Record<string, "boot" | "shutdown">>({});

  const refresh = useCallback(async () => {
    try {
      const list = await listSimulators();
      setSims(list);
      setError(null);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
      setError(msg);
    }
  }, []);

  // One-shot platform probe on mount. Server returns 501 on non-macOS
  // so we can hide the section entirely instead of rendering an empty
  // "0 installed" state that suggests Xcode is missing.
  useEffect(() => {
    let cancelled = false;
    listSimulators()
      .then((list) => {
        if (cancelled) return;
        setSupported(true);
        setSims(list);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 501) {
          setSupported(false);
          return;
        }
        setSupported(true); // transient error — keep the UI visible
        const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only poll while the section is open — no reason to keep hitting
  // simctl in the background when the user has it collapsed.
  useEffect(() => {
    if (!open || !supported) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await refresh();
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, supported, refresh]);

  const sorted = useMemo(() => (sims ? sortSims(sims) : null), [sims]);

  // Hide entirely on non-macOS servers. Must come *after* every hook
  // call above so the hook count is stable across renders — swapping
  // the order is React error #300.
  if (supported === false) return null;

  const handleBoot = async (udid: string) => {
    setPending((p) => ({ ...p, [udid]: "boot" }));
    try {
      await bootSimulator(udid);
      await refresh();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
      setError(msg);
    } finally {
      setPending((p) => {
        const { [udid]: _drop, ...rest } = p;
        return rest;
      });
    }
  };

  const handleShutdown = async (udid: string) => {
    setPending((p) => ({ ...p, [udid]: "shutdown" }));
    try {
      await shutdownSimulator(udid);
      await refresh();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
      setError(msg);
    } finally {
      setPending((p) => {
        const { [udid]: _drop, ...rest } = p;
        return rest;
      });
    }
  };

  return (
    <div className="mt-8 border-t border-surface-800 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm text-surface-300 hover:text-surface-100 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        <span className="font-medium">iOS Simulators</span>
        {sims && (
          <span className="text-xs text-surface-500">
            ({sims.length} installed)
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3">
          {error && (
            <div className="mb-3 p-2 rounded-md border border-rose-500/30 bg-rose-500/10 text-xs text-rose-300">
              {error}
            </div>
          )}

          {sims === null && !error && (
            <div className="text-xs text-surface-500 italic flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading simulators…
            </div>
          )}

          {sims && sims.length === 0 && !error && (
            <div className="text-xs text-surface-500 italic">
              No simulators installed. Install Xcode to add iOS runtimes.
            </div>
          )}

          <div className="space-y-1.5">
            {sorted?.map((sim) => {
              const act = pending[sim.udid];
              const booted = sim.state === "Booted";
              return (
                <div
                  key={sim.udid}
                  className="flex items-center gap-3 p-2 rounded-md border border-surface-800 bg-surface-900/40"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-surface-100 truncate">
                      {sim.name}
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-800 text-surface-400 align-middle">
                        iOS {sim.osVersion}
                      </span>
                      {booted && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 align-middle">
                          Booted
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-surface-600 truncate">
                      {sim.udid}
                    </div>
                  </div>
                  {booted ? (
                    <button
                      type="button"
                      onClick={() => handleShutdown(sim.udid)}
                      disabled={!!act}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-surface-800 text-surface-300 border border-surface-700 hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {act === "shutdown" ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Power className="w-3 h-3" />
                      )}
                      Shutdown
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleBoot(sim.udid)}
                      disabled={!!act}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {act === "boot" ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                      Boot
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
